import { Box, Button, CircularProgress, Grid, Paper, ThemeProvider, Tooltip, Typography } from "@mui/material";
import { DateTime } from "luxon";
import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { Defined } from "sonobello.utilities.react";

import { Theme } from "../../Assets/theme/Theme";
import { SessionContext } from "../../Context/SessionContext";
import { UserContext } from "../../Context/UserContext";
import { Appointment, EnhancedAppointment } from "../../Models/Appointment";
import { BlockType } from "../../Models/Block";
import { EnhancedCalendar } from "../../Models/Calendar";
import { Customer } from "../../Models/Customer";
import { EnhancedOpsCalendar, OpsCalendar } from "../../Models/OpsCalendar";
import { OpsReservation } from "../../Models/OpsReservation";
import { OpsServiceCategory } from "../../Models/OpsService";
import { EnhancedOpsSlot, Slot } from "../../Models/OpsSlot";
import { Service } from "../../Models/Service";
import { ServiceGroup } from "../../Models/ServiceGroup";
import { ISignalRSlotUpdate } from "../../Types/SignalRSlotUpdate";
import { StepId } from "../../Types/Step";
import { Error } from "../../Utils/Errors";
import OpsEndpointPathsUtility from "../../Utils/OpsEndpointPathsUtility";
import OpsMappingUtility from "../../Utils/OpsMappingUtility";
import { useApi } from "../../Utils/useApi";
import ComposedAppointmentNoteCard from "../../V2/Playbook/Booking/Compositions/ComposedAppointmentNoteCard";
import useResilientGetCalendar from "../../V2/Playbook/Booking/Compositions/useResilientGetCalendar";
import DaySelector from "./BookingWizardDaySelector";
import BookingWizardPreviousAppts from "./BookingWizardPreviousAppts";
import ServiceSelector from "./BookingWizardServiceSelector";
import TimeSelector from "./BookingWizardTimeSelector";

/** An event which describes a change to the occupation state of a scheduling slot. */
export interface ISlotUpdateEvent {
  /** A flag indicating if the slot update was spawned by the customer currently being booked. */
  isSlotReservedByCustomer: boolean;
  /** The unique identifier of the slot in the OPS system. */
  slotId: number;
  /** The timestamp of the change event. */
  timestamp: DateTime;
  /** The updated state of the slot. */
  type: BlockType;
}

const slotPadding = Number(process.env.REACT_APP_TIMESLOT_PADDING_MINUTES || 5);

/**
 * Validates the POST request for the customer appointment.
 * @param appointment The appointment object to check.
 * @param leadId The lead id to check.
 * @param slot The slot object to check.
 * @param centerId The center id to check.
 */
const validatePostCustomerAppointment = (appointment?: Appointment, leadId?: string, slot?: EnhancedOpsSlot): boolean =>
  !!appointment && !!leadId && !!slot?.id;

const BookingWizard: React.FC = () => {
  const {
    appointment,
    apptErrorCenters,
    bookingDisquals,
    customer,
    isCustomerValidForOpsBooking,
    lead,
    step,
    setAppointment,
    setApptErrorCenters,
    setShowBookingWizard
  } = useContext(SessionContext);

  const [saveAppointment, setSaveAppointment] = useState<Appointment>();
  const [rescheduleAppointment, setRescheduleAppointment] = useState<Appointment>();
  const [apptNote, setApptNote] = useState<string>("");
  const [calendar, setCalendar] = useState<EnhancedOpsCalendar | EnhancedCalendar>();
  const [slotUpdateEvent, setSlotUpdateEvent] = useState<ISlotUpdateEvent>();
  const [releaseReservation, setReleaseReservation] = useState<OpsReservation>();
  const [service, setService] = useState<OpsServiceCategory>();
  const [services, setServices] = useState<ServiceGroup[]>();
  const [selectedDay, setSelectedDay] = useState<DateTime>();
  const [selectedSlot, setSelectedSlot] = useState<EnhancedOpsSlot>();
  const [bookingSlot, setBookingSlot] = useState<EnhancedOpsSlot>();
  const serviceRef = useRef(service);
  const centerRef = useRef(customer.center);
  const stepRef = useRef(step);
  useEffect(() => {
    stepRef.current = step;
  }, [step]);
  const slotUpdateEventRef = useRef(slotUpdateEvent);

  const getCurrentService = useCallback(() => {
    return serviceRef.current ? serviceRef.current : undefined;
  }, [serviceRef.current]);

  const getTimeslots = (
    calendar: EnhancedOpsCalendar | EnhancedCalendar,
    service?: OpsServiceCategory
  ): EnhancedOpsSlot[] => {
    const schedules = service ? calendar.serviceSchedules[service?.id] : [];
    const timeslots = schedules.find(d => d.date.toISODate() === selectedDay?.toISODate())?.blocks || [];

    return timeslots as unknown as EnhancedOpsSlot[];
  };

  // SUBSCRIBE TO SIGNALR UPDATE CALENDAR TOPIC
  const {
    signalR: { payloads, subscribe, unsubscribe }
  } = useContext(UserContext);
  useEffect(() => {
    const topics = [`update_${customer.centerId}`];
    subscribe(topics);
    return () => unsubscribe(topics);
  }, [subscribe, unsubscribe, step, customer.centerId]);
  const signalRUpdate = payloads[`update_${customer.centerId}`] as ISignalRSlotUpdate | null;

  // ON SIGNALR TOPIC UPDATE
  useEffect(() => {
    if (!signalRUpdate) return;
    // if timestamp is most recent trigger a slot update
    const updateTimestamp = DateTime.fromISO(signalRUpdate.timestampUtc);
    setSlotUpdateEvent(previousUpdate => {
      if (customer.id && (!previousUpdate || updateTimestamp > previousUpdate.timestamp)) {
        const newUpdateEvent: ISlotUpdateEvent = {
          isSlotReservedByCustomer: (signalRUpdate?.associatedCustomerIds || [])?.some(id => id === customer.id),
          slotId: signalRUpdate?.slotId,
          timestamp: updateTimestamp,
          type: signalRUpdate?.type
        };
        slotUpdateEventRef.current = newUpdateEvent;
        return newUpdateEvent;
      }
      return previousUpdate;
    });
  }, [signalRUpdate]);

  // ON CENTER CHANGED
  useEffect(() => {
    // reset all of the wizard states
    const service = getCurrentService();
    const centerId = centerRef.current?.id;
    setSelectedSlot(oldSlot => {
      // if a time was reserved, release it
      if (oldSlot && stepRef.current.id === StepId.Booking)
        setReleaseReservation(new OpsReservation(centerId as string, service?.id, oldSlot));
      return undefined;
    });
    centerRef.current = customer.center;
    setSelectedDay(undefined);
    setServices(undefined);
    setCalendar(undefined);
    handleService(undefined);
    slotUpdateEventRef.current = undefined;
    setSlotUpdateEvent(undefined);
    if (rescheduleAppointment?.centerId !== customer.centerId) setRescheduleAppointment(undefined);
  }, [customer.centerId]);

  // GET CALENDAR

  const {
    result: calendarResponse,
    error: calendarError,
    execute: fetchCalendar
  } = useResilientGetCalendar({
    primaryHookProps: { contactId: lead.id },
    secondaryHookProps: { contactId: lead.id, customer: customer as Customer & Defined<Pick<Customer, "id">> }
  });
  useEffect(() => {
    if (
      isCustomerValidForOpsBooking &&
      customer.id &&
      customer.centerId &&
      step.id === StepId.Booking &&
      !apptErrorCenters.some(id => id === customer.centerId)
    ) {
      fetchCalendar({ customerId: customer.id, customerCenterId: customer.centerId });
    }
  }, [isCustomerValidForOpsBooking, customer.centerId, customer.id, step.id, apptErrorCenters]);

  useEffect(() => {
    if (!calendarResponse || !centerRef.current) return;
    const enhancedCalendar = OpsMappingUtility.toBookingCalendar(calendarResponse, centerRef.current.luxonTimeZone);
    const services = enhancedCalendar.calendarServices;

    setService(oldService => {
      // if selected service is already set and is still valid, do nothing
      if (services.some(s => s.service.id === oldService?.id && !s.disabled)) return oldService;
      const filteredServices = services.filter(s => !s.disabled);

      // otherwise set the default service. Use the one from the lead, otherwise the first one.
      const service = (
        filteredServices.find(
          ({ service }) => service?.businessUnit?.id === lead.campaign.businessUnit.id && service.isDefault
        ) || filteredServices[0]
      )?.service;
      serviceRef.current = service;
      return service;
    });
    setServices(services);
    setCalendar(enhancedCalendar);
  }, [calendarResponse]);

  useEffect(() => {
    if (!calendar || !centerRef.current) return;

    /** Take previous calendar and use it to repaint for ops */
    const enhancedCalendar = OpsMappingUtility.toBookingCalendar(
      calendar as OpsCalendar,
      centerRef.current.luxonTimeZone,
      slotUpdateEvent
    );

    setCalendar(enhancedCalendar);
  }, [slotUpdateEvent]);

  useEffect(() => {
    if (calendarError) {
      // if 404 on getting an updated calendar, do a normal GetCalendar
      if (calendarError.response?.status === 404 && slotUpdateEventRef.current) {
        slotUpdateEventRef.current = undefined;
        setSlotUpdateEvent(undefined);
      } else {
        const centerId = centerRef.current?.id;
        const service = getCurrentService();
        setSelectedDay(oldStartTime => {
          // if a reservation exists release it
          if (oldStartTime && selectedSlot)
            setReleaseReservation(new OpsReservation(centerId as string, service?.id || "", selectedSlot));
          return undefined;
        });
        setApptErrorCenters(c => (centerRef.current ? [...c, centerRef.current.id] : c));
      }
    }
  }, [calendarError, setApptErrorCenters]);

  // RELEASE RESERVATIONS
  useApi<unknown, Slot>({
    method: "post",
    url: OpsEndpointPathsUtility.postAppointmentReservationUrl(
      lead.id,
      customer.id,
      releaseReservation?.centerId,
      releaseReservation?.serviceId,
      releaseReservation?.slot.id
    ),
    data: releaseReservation?.slot as Slot,
    valid: !!releaseReservation,
    error: Error.PostCenterRelease
  });

  // POST CUSTOMER APPOINTMENT
  const {
    response: apptResponse,
    isLoading: bookingLoading,
    error: bookingError
  } = useApi<Appointment, Appointment>({
    method: saveAppointment?.id ? "put" : "post",
    url: OpsEndpointPathsUtility.postAppointmentUrl(
      lead.id,
      saveAppointment?.customerId,
      saveAppointment?.centerId || customer.centerId,
      saveAppointment?.id,
      bookingSlot?.id
    ),
    data: saveAppointment,
    valid: validatePostCustomerAppointment(saveAppointment, lead.id, bookingSlot),
    error: Error.PostCustomerAppointment
  });
  useEffect(() => {
    if (apptResponse?.id) {
      // on successful POST, save the response and exit the wizard
      const service = getCurrentService();
      toast.success("The appointment was booked successfully!");
      setApptErrorCenters([]);
      setShowBookingWizard(false);
      setSelectedSlot(undefined);
      setAppointment({
        id: apptResponse?.id,
        businessUnit: service?.businessUnit?.name || "",
        serviceSubCategory: service?.name || ""
      });
      setRescheduleAppointment(undefined);
      setBookingSlot(undefined);
    }
  }, [apptResponse, setAppointment, setApptErrorCenters, setShowBookingWizard]);
  useEffect(() => {
    if (bookingError) {
      setSelectedSlot(oldSlot => {
        // if a time was reserved, release it
        if (oldSlot && centerRef.current) {
          const service = getCurrentService();
          setReleaseReservation(new OpsReservation(centerRef.current.id, service?.id || "", oldSlot));
        }
        return undefined;
      });
      // if error is not recoverable, add center to the error list
      if (bookingError?.response?.status !== 409)
        setApptErrorCenters(c => (centerRef.current ? [...c, centerRef.current.id] : c));
    }
  }, [bookingError, setApptErrorCenters, setShowBookingWizard]);

  const handleService = (service?: ServiceGroup) => {
    setSelectedDay(undefined);
    serviceRef.current = service?.service;
    setService(service?.service);
  };

  const handleCancel = () => {
    // release the reservation if it exists
    setSelectedSlot(oldSlot => {
      if (oldSlot?.startTime && centerRef.current) {
        const service = getCurrentService();
        setReleaseReservation(new OpsReservation(centerRef.current.id, service?.id || "", oldSlot));
      }
      return undefined;
    });
    setShowBookingWizard(false);
  };

  const handleConfirm = () => {
    if (selectedSlot?.startTime) {
      // if the reservation was created too long ago, deselect it
      if (
        selectedSlot.startTime.diff(DateTime.now().setZone(customer.center?.luxonTimeZone.name), "minutes").minutes <= 5
      ) {
        toast.warning("The reservation for the selected timeslot expired. Please select a new one.");
        setSelectedSlot(undefined);
      }
      // else if the reservation now violates the selection padding, deselect it
      else if (selectedSlot.startTime <= DateTime.now().plus({ minutes: slotPadding })) {
        toast.error("The selected time is no longer valid. Please choose a new one.");
        setSelectedSlot(undefined);
      }
      // otherwise, POST the appointment
      else {
        setBookingSlot(selectedSlot);
        setSaveAppointment({
          id: rescheduleAppointment ? rescheduleAppointment.id : null,
          centerId: customer.centerId as string,
          customerId: customer.id as string,
          startTimeUtc: selectedSlot.startTimeUtc,
          endTimeUtc: selectedSlot.endTimeUtc,

          services: [service as Service],
          note: apptNote,
          // MUST have a valid opportunity id to attach easyPathInput for Double Booker
          // if not the backend will simply schedule in the primary room of the facility
          easyPathInput: customer.opportunity?.id
            ? {
                opportunityId: customer.opportunity.id,
                dob: customer.dateOfBirth as string,
                email: customer.email,
                leadId: lead.id,
                phoneNumber: customer.phoneNumber,
                zipCode: customer.zipCode
              }
            : null
        });
      }
    }
  };

  const handleReschedule = (appointment?: EnhancedAppointment) => {
    if (appointment) setService(appointment.services.at(0) as OpsServiceCategory);
    setRescheduleAppointment(appointment);
  };

  const allDisquals = [...bookingDisquals];
  if (!customer.zipCode) allDisquals.push("Missing Zip Code");

  return (
    <ThemeProvider theme={Theme}>
      <Paper sx={{ height: "100%", mt: 1 }}>
        <Grid container sx={{ height: "100%", p: 2 }} direction="column" rowSpacing={1} wrap="nowrap">
          {bookingLoading ? (
            <Grid
              item
              container
              justifyContent="center"
              alignItems="center"
              direction="column"
              sx={{ height: "100%" }}
              spacing={2}
            >
              <Grid item>
                <Typography variant="body1">Saving changes to the appointment.</Typography>
              </Grid>
              <Grid item>
                <CircularProgress size="4rem" />
              </Grid>
            </Grid>
          ) : (
            <>
              {!!services && !!calendar && !!customer.center ? (
                <>
                  {/* Hard set the height of this container since the carousel component's html formatting is abnormal. */}
                  <Grid item sx={{ height: "75px" }}>
                    <Box pt={1}>
                      <DaySelector
                        selectedDay={selectedDay}
                        disabled={bookingLoading}
                        serviceSchedule={service ? calendar.serviceSchedules[service?.id] : []}
                        setSelectedDay={setSelectedDay}
                      />
                    </Box>
                  </Grid>
                  <Grid container item columnSpacing={1} xs>
                    <Grid item xs={4}>
                      <Grid container direction="column" rowSpacing={2} wrap="nowrap" sx={{ height: "100%" }}>
                        <Grid container item direction="column" alignItems="center">
                          <ServiceSelector
                            disabled={bookingLoading}
                            serviceId={service?.id}
                            options={services || []}
                            setService={handleService}
                          />
                        </Grid>
                        <Grid item xs>
                          <Paper sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
                            <Box p={2} display="flex" flexDirection="column" flex="1 1 0" minHeight={0}>
                              <ComposedAppointmentNoteCard disabled={bookingLoading} onChange={setApptNote} />
                            </Box>
                          </Paper>
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid item xs={3}>
                      <Paper sx={{ height: "100%" }}>
                        <Grid
                          container
                          direction="column"
                          rowSpacing={2}
                          wrap="nowrap"
                          sx={{ m: 0, p: 2, height: "100%" }}
                        >
                          <Grid item>
                            <Typography align="center" variant="body1">
                              <strong>Consult Time:</strong>
                            </Typography>
                          </Grid>
                          <Grid sx={{ overflow: "auto" }} item xs>
                            <TimeSelector
                              disabled={bookingLoading}
                              preventAutoSelect={Boolean(appointment)}
                              service={service}
                              selectedSlot={selectedSlot}
                              timeslots={(getTimeslots(calendar, service) || []) as EnhancedOpsSlot[]}
                              setSelectedSlot={setSelectedSlot}
                            />
                          </Grid>
                        </Grid>
                      </Paper>
                    </Grid>
                    <Grid item xs>
                      <Paper sx={{ height: "100%", pl: 2 }}>
                        <BookingWizardPreviousAppts
                          enabledServices={services.filter(s => !s.disabled).map(s => s)}
                          reschedulingApptId={rescheduleAppointment?.id || undefined}
                          setRescheduleAppointment={handleReschedule}
                        />
                      </Paper>
                    </Grid>
                  </Grid>
                </>
              ) : (
                <Grid container item xs justifyContent="center" alignItems="center">
                  <Box m={5}>
                    <CircularProgress />
                  </Box>
                </Grid>
              )}
              <Grid item>
                <Box pb={1}>
                  <Grid container sx={{ pr: 1 }} spacing={1} justifyContent="flex-end" alignItems="center">
                    <Grid item>
                      <Button color="secondary" disabled={bookingLoading} onClick={handleCancel}>
                        Cancel
                      </Button>
                    </Grid>
                    <Grid item>
                      <Tooltip title={allDisquals.length ? allDisquals.map((d, i) => <div key={i}>{d}</div>) : ""}>
                        <span>
                          <Button disabled={!selectedSlot || !!allDisquals.length} onClick={handleConfirm}>
                            {rescheduleAppointment ? "Reschedule Appointment" : "Book Appointment"}
                          </Button>
                        </span>
                      </Tooltip>
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
            </>
          )}
        </Grid>
      </Paper>
    </ThemeProvider>
  );
};

export default BookingWizard;
